import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AgentProxyService } from '@/services/agentProxyService';
import { getFilterSettings } from '@/utils/databaseFilterStorage';
import { useDataSourceStore } from '@/stores/dataSourceStore';

interface EditQueryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string; databaseName?: string; schemaName?: string }) => Promise<void> | void;
  title: string;
  currentName: string;
  currentDescription?: string | null;
  currentDatabaseName?: string | null;
  currentSchemaName?: string | null;
  dataSourceId?: string;
  dataSourceType?: string;
  namePlaceholder?: string;
  descriptionPlaceholder?: string;
  maxNameLength?: number;
  maxDescriptionLength?: number;
  showDatabaseSelection?: boolean;
}

export const EditQueryModal: React.FC<EditQueryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  currentName,
  currentDescription,
  currentDatabaseName,
  currentSchemaName,
  dataSourceId,
  dataSourceType,
  namePlaceholder = 'Enter query name',
  descriptionPlaceholder = 'Enter query description (optional)',
  maxNameLength = 70,
  maxDescriptionLength = 100,
  showDatabaseSelection = false
}) => {
  const [name, setName] = useState(currentName);
  const [description, setDescription] = useState(currentDescription || '');
  const [databaseName, setDatabaseName] = useState(currentDatabaseName || '');
  const [schemaName, setSchemaName] = useState(currentSchemaName || '');
  const [databases, setDatabases] = useState<string[]>([]);
  const [schemas, setSchemas] = useState<string[]>([]);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(false);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Load databases when database selection is enabled
  const loadDatabases = useCallback(async () => {
    if (!dataSourceId) {
      console.log('[EditQueryModal] No dataSourceId provided');
      return;
    }
    
    // Get the datasource to determine the correct type
    const dataSourceStore = useDataSourceStore.getState();
    let actualDataSourceType = dataSourceType?.toLowerCase();
    
    // If type not provided or datasource not loaded, fetch it
    if (!actualDataSourceType || !dataSourceStore.dataSources.has(dataSourceId)) {
      await dataSourceStore.fetchDataSource(dataSourceId);
      const dataSource = dataSourceStore.dataSources.get(dataSourceId);
      // Normalize to lowercase for consistent comparison
      actualDataSourceType = dataSource?.databaseType?.toLowerCase();
    }
    
    console.log('[EditQueryModal] Loading databases for dataSourceId:', dataSourceId, 'type:', actualDataSourceType);
    setIsLoadingDatabases(true);
    try {
      const response = await AgentProxyService.getDatabases(dataSourceId);
      console.log('[EditQueryModal] Databases response:', response);
      
      // Handle different response formats
      let databaseList: { name: string }[] = [];
      
      if (Array.isArray(response)) {
        // Direct array response
        databaseList = response
          .map((db) => {
            if (typeof db === 'string') return { name: db };
            if (db && typeof db === 'object' && 'name' in db) {
              const n = (db as { name?: unknown }).name;
              if (typeof n === 'string') return { name: n };
            }
            return undefined;
          })
          .filter((db): db is { name: string } => !!db && typeof db.name === 'string');
      } else if (response && typeof response === 'object') {
        const responseObj: Record<string, unknown> = ((): Record<string, unknown> => {
          const maybe = response as unknown
          return (typeof maybe === 'object' && maybe !== null && !Array.isArray(maybe)) ? (maybe as Record<string, unknown>) : {}
        })();
        if (Array.isArray((responseObj as { databases?: unknown[] }).databases)) {
          const arr = (responseObj as { databases?: unknown[] }).databases as unknown[];
          databaseList = arr.map((db) => {
            if (typeof db === 'string') return { name: db }
            if (db && typeof db === 'object' && 'name' in db) {
              const n = (db as { name?: unknown }).name
              if (typeof n === 'string') return { name: n }
            }
            return undefined
          }).filter((x): x is { name: string } => !!x && typeof x.name === 'string');
        } else if (responseObj.data && typeof responseObj.data === 'object') {
          const dataObjUnknown = responseObj.data
          const dataObj = (dataObjUnknown && typeof dataObjUnknown === 'object' && !Array.isArray(dataObjUnknown)) ? (dataObjUnknown as Record<string, unknown>) : {}
          if (Array.isArray((dataObj as { databases?: unknown[] }).databases)) {
            const arrUnknown = (dataObj as { databases?: unknown[] }).databases
            const arr = Array.isArray(arrUnknown) ? arrUnknown : []
            databaseList = arr.map((db) => {
              if (typeof db === 'string') return { name: db }
              if (db && typeof db === 'object') {
                const n = (db as { name?: unknown }).name
                if (typeof n === 'string') return { name: n }
              }
              return undefined
            }).filter((x): x is { name: string } => !!x && typeof x.name === 'string');
          }
        } else if (Array.isArray((responseObj as { data?: unknown[] }).data)) {
          const arrUnknown = (responseObj as { data?: unknown[] }).data
          const arr = Array.isArray(arrUnknown) ? arrUnknown : []
          databaseList = arr.map((db) => {
            if (typeof db === 'string') return { name: db }
            if (db && typeof db === 'object') {
              const n = (db as { name?: unknown }).name
              if (typeof n === 'string') return { name: n }
            }
            return undefined
          }).filter((x): x is { name: string } => !!x && typeof x.name === 'string');
        }
      }
      
      console.log('[EditQueryModal] Parsed database list:', databaseList);
      if (databaseList.length > 0) {
        const names = databaseList.map((db) => typeof db === 'string' ? db : db.name);
        
        // Apply filter settings to hide hidden databases
        // Use the actual database type we determined (normalize to lowercase)
        const dbType: 'mysql' | 'postgresql' = actualDataSourceType?.toLowerCase() === 'postgresql' ? 'postgresql' : 'mysql';
        const filterSettings = getFilterSettings(dataSourceId, dbType);
        const visibleDatabases = names.filter(dbName => !filterSettings.hiddenDatabases.includes(dbName));
        
        console.log('[EditQueryModal] Actual database type:', dbType);
        console.log('[EditQueryModal] All databases:', names);
        console.log('[EditQueryModal] Hidden databases:', filterSettings.hiddenDatabases);
        console.log('[EditQueryModal] Visible databases after filter:', visibleDatabases);
        setDatabases(visibleDatabases);
      } else {
        console.log('[EditQueryModal] No databases found in response');
        setDatabases([]);
      }
    } catch (err) {
      console.error('Failed to load databases:', err);
    } finally {
      setIsLoadingDatabases(false);
    }
  }, [dataSourceId, dataSourceType]);

  // Load schemas when database changes (PostgreSQL only)
  const loadSchemas = useCallback(async () => {
    // Get the actual datasource type
    const dataSourceStore = useDataSourceStore.getState();
    let actualDataSourceType = dataSourceType?.toLowerCase();
    
    if (!dataSourceId) return;
    
    // Ensure we have the datasource info
    if (!actualDataSourceType || !dataSourceStore.dataSources.has(dataSourceId)) {
      await dataSourceStore.fetchDataSource(dataSourceId);
      const dataSource = dataSourceStore.dataSources.get(dataSourceId);
      // Normalize to lowercase for consistent comparison
      actualDataSourceType = dataSource?.databaseType?.toLowerCase();
    }
    
    // Only load schemas for PostgreSQL
    if (!databaseName || actualDataSourceType?.toLowerCase() !== 'postgresql') {
      setSchemas([]);
      return;
    }
    
    console.log('[EditQueryModal] Loading schemas for database:', databaseName, 'type:', actualDataSourceType);
    setIsLoadingSchemas(true);
    try {
      const response = await AgentProxyService.getSchemas(dataSourceId, databaseName);
      console.log('[EditQueryModal] Schemas response:', response);
      
      // Handle different response formats
      let schemaList: { name: string }[] = [];
      
      if (Array.isArray(response)) {
        // Direct array response
        schemaList = response
          .map((s) => {
            if (typeof s === 'string') return { name: s }
            if (s && typeof s === 'object') {
              const n = (s as { name?: unknown }).name
              if (typeof n === 'string') return { name: n }
            }
            return undefined
          })
          .filter((s): s is { name: string } => !!s);
      } else if (response && typeof response === 'object') {
        const responseObj = (response && typeof response === 'object') ? (response as Record<string, unknown>) : {};
        if (Array.isArray((responseObj as { schemas?: unknown[] }).schemas)) {
          const arrUnknown = (responseObj as { schemas?: unknown[] }).schemas;
          const arr = Array.isArray(arrUnknown) ? arrUnknown : [];
          schemaList = arr.map((s) => {
            if (typeof s === 'string') return { name: s };
            if (s && typeof s === 'object' && 'name' in s) {
              const n = (s as { name?: unknown }).name;
              if (typeof n === 'string') return { name: n };
            }
            return undefined;
          }).filter((x): x is { name: string } => !!x && typeof x.name === 'string');
        } else if (responseObj.data && typeof responseObj.data === 'object') {
          const dataObjUnknown = responseObj.data;
          const dataObj = (dataObjUnknown && typeof dataObjUnknown === 'object') ? (dataObjUnknown as Record<string, unknown>) : {};
          if (Array.isArray((dataObj as { schemas?: unknown[] }).schemas)) {
            const arrUnknown = (dataObj as { schemas?: unknown[] }).schemas;
            const arr = Array.isArray(arrUnknown) ? arrUnknown : [];
            schemaList = arr.map((s) => {
              if (typeof s === 'string') return { name: s };
              if (s && typeof s === 'object' && 'name' in s) {
                const n = (s as { name?: unknown }).name;
                if (typeof n === 'string') return { name: n };
              }
              return undefined;
            }).filter((x): x is { name: string } => !!x && typeof x.name === 'string');
          }
        } else if (Array.isArray((responseObj as { data?: unknown[] }).data)) {
          const arrUnknown = (responseObj as { data?: unknown[] }).data;
          const arr = Array.isArray(arrUnknown) ? arrUnknown : [];
          schemaList = arr.map((s) => {
            if (typeof s === 'string') return { name: s };
            if (s && typeof s === 'object' && 'name' in s) {
              const n = (s as { name?: unknown }).name;
              if (typeof n === 'string') return { name: n };
            }
            return undefined;
          }).filter((x): x is { name: string } => !!x && typeof x.name === 'string');
        }
      }
      
      if (schemaList.length > 0) {
        const names = schemaList.map((schema) => typeof schema === 'string' ? schema : schema.name);
        
        // Apply filter settings to hide hidden schemas
        const filterSettings = getFilterSettings(dataSourceId, 'postgresql');
        // Check both database-specific hidden schemas and global hidden schemas (using '*' as key)
        const dbHiddenSchemas = filterSettings.hiddenSchemas[databaseName] || [];
        const globalHiddenSchemas = filterSettings.hiddenSchemas['*'] || [];
        const allHiddenSchemas = [...new Set([...dbHiddenSchemas, ...globalHiddenSchemas])];
        const visibleSchemas = names.filter(schemaName => !allHiddenSchemas.includes(schemaName));
        
        console.log('[EditQueryModal] All schemas:', names);
        console.log('[EditQueryModal] Hidden schemas for', databaseName, ':', dbHiddenSchemas);
        console.log('[EditQueryModal] Global hidden schemas:', globalHiddenSchemas);
        console.log('[EditQueryModal] Visible schemas after filter:', visibleSchemas);
        setSchemas(visibleSchemas);
      } else {
        console.log('[EditQueryModal] No schemas found');
        setSchemas([]);
      }
    } catch (err) {
      console.error('Failed to load schemas:', err);
      setSchemas([]);
    } finally {
      setIsLoadingSchemas(false);
    }
  }, [dataSourceId, databaseName, dataSourceType]);

  useEffect(() => {
    if (isOpen) {
      setName(currentName);
      setDescription(currentDescription || '');
      setDatabaseName(currentDatabaseName || '');
      setSchemaName(currentSchemaName || '');
      setError('');
    }
  }, [isOpen, currentName, currentDescription, currentDatabaseName, currentSchemaName]);

  // Load databases when modal opens with database selection enabled
  useEffect(() => {
    if (isOpen && showDatabaseSelection && dataSourceId) {
      void loadDatabases();
    }
  }, [isOpen, showDatabaseSelection, dataSourceId, loadDatabases]);

  // Load schemas when database changes
  useEffect(() => {
    if (showDatabaseSelection && databaseName && dataSourceType === 'postgresql') {
      void loadSchemas();
    } else {
      setSchemas([]);
      setSchemaName('');
    }
  }, [databaseName, showDatabaseSelection, dataSourceType, loadSchemas]);


  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedDatabase = databaseName.trim();
    const trimmedSchema = schemaName.trim();
    
    if (!trimmedName) {
      setError('Name is required');
      return;
    }

    // Check if nothing changed
    if (trimmedName === currentName.trim() && 
        trimmedDescription === (currentDescription?.trim() || '') &&
        trimmedDatabase === (currentDatabaseName?.trim() || '') &&
        trimmedSchema === (currentSchemaName?.trim() || '')) {
      onClose();
      return;
    }

    setIsLoading(true);
    setError('');

    void (async () => {
      try {
        const saveData: { name: string; description: string; databaseName?: string; schemaName?: string } = {
          name: trimmedName,
          description: trimmedDescription
        };
        
        // Include database and schema if selection is enabled
        if (showDatabaseSelection) {
          if (trimmedDatabase) {
            saveData.databaseName = trimmedDatabase;
          }
          if (trimmedSchema && dataSourceType === 'postgresql') {
            saveData.schemaName = trimmedSchema;
          }
        }
        
        await onSave(saveData);
        onClose();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [name, description, databaseName, schemaName, currentName, currentDescription, currentDatabaseName, currentSchemaName, dataSourceType, showDatabaseSelection, onSave, onClose]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  }, []);

  const handleDatabaseChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDatabaseName(e.target.value);
  }, []);

  const handleSchemaChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSchemaName(e.target.value);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <button type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleFormSubmit} className="p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              onKeyDown={handleKeyDown}
              placeholder={namePlaceholder}
              maxLength={maxNameLength}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <span className={`text-xs ${description.length > maxDescriptionLength ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {description.length}/{maxDescriptionLength}
              </span>
            </div>
            <textarea
              value={description}
              onChange={handleDescriptionChange}
              onKeyDown={handleKeyDown}
              placeholder={descriptionPlaceholder}
              maxLength={maxDescriptionLength}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Database Selection */}
          {showDatabaseSelection && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Database
                </label>
                <select
                  value={databaseName}
                  onChange={handleDatabaseChange}
                  disabled={isLoadingDatabases}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select database (optional)</option>
                  {databases.map((db) => (
                    <option key={db} value={db}>
                      {db}
                    </option>
                  ))}
                </select>
              </div>

              {/* Schema Selection (PostgreSQL only) */}
              {dataSourceType === 'postgresql' && databaseName && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Schema
                  </label>
                  <select
                    value={schemaName}
                    onChange={handleSchemaChange}
                    disabled={isLoadingSchemas}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select schema (optional)</option>
                    {schemas.map((schema) => (
                      <option key={schema} value={schema}>
                        {schema}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {error && (
            <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};